import { DeleteOutlined } from '@ant-design/icons'
import { Button, Checkbox, InputNumber, Segmented, TimePicker } from 'antd'
import { DatePicker } from 'common/components/DatePicker/DatePicker'
import { Input } from 'common/components/Input/Input'
import { Label } from 'common/components/Label/Label'
import { Select } from 'common/components/Select/Select'
import { TIME_FORMAT } from 'common/constants/timeFormat'
import dayjs from 'dayjs'
import { useFormik } from 'formik'
import type { IPatientConsultationStaff } from 'features/Home/interfaces/IInfoPatient'
import type { AvailabilityKind, VisitKind } from 'features/Schedule/api/availabilitySeries.types'
import {
  applyDraftPatch,
  checkboxWeekdays,
  draftEndsLabel,
  draftSummary,
  everyOptions,
  isInOfficeKind,
  nthChoices,
  REPEAT_UNITS,
  sortVisitKinds,
  WEEKDAY_SHORT,
  type BlockDraft,
  type DraftRepeat,
  type DraftUnit,
} from './availabilitySeries.utils'
import styles from './doctorSchedule.module.scss'

type DoctorScheduleFormProps = {
  draft: BlockDraft
  kinds: AvailabilityKind[]
  visitKinds: VisitKind[]
  providers: IPatientConsultationStaff[]
  siteName: string
  saving: boolean
  fieldErrors: Record<string, string>
  onSave: (values: BlockDraft) => void
  onCancel: () => void
  onDelete: () => void
  onClearFieldErrors: () => void
  locked?: boolean
}

const WEEK_EVERY = [1, 2, 3, 4].map((value) => ({
  value,
  title: value === 1 ? 'Every week' : `Every ${value} weeks`,
  label: value === 1 ? 'Every week' : `Every ${value} weeks`,
}))

const field = (errors: Record<string, string>, key: string) =>
  errors[key] ? <div className={styles.fieldError}>{errors[key]}</div> : null

const kindOptions = (kinds: AvailabilityKind[], withDot: boolean) =>
  kinds.map((kind) => ({
    value: kind.id,
    title: kind.name,
    label: withDot ? (
      <span className={styles.kindCell}>
        <span className={styles.dot} style={{ background: kind.color }} />
        {kind.name}
      </span>
    ) : (
      kind.name
    ),
  }))

export const DoctorScheduleForm = ({
  draft: initialDraft,
  kinds,
  visitKinds,
  providers,
  siteName,
  saving,
  fieldErrors,
  onSave,
  onCancel,
  onDelete,
  onClearFieldErrors,
  locked = false,
}: DoctorScheduleFormProps) => {
  const formik = useFormik<BlockDraft>({
    initialValues: initialDraft,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: onSave,
  })
  const draft = formik.values
  const update = (patch: Partial<BlockDraft>) => {
    formik.setValues((current) => applyDraftPatch(current, patch))
    onClearFieldErrors()
  }
  const repeating = draft.repeat !== 'none'
  const choices = nthChoices(draft.date)
  const unitOptions: { value: DraftUnit; label: string; title: string }[] = [
    ...REPEAT_UNITS.map((unit) => ({ value: unit, label: unit, title: unit })),
    ...choices.map((choice) => ({ value: choice.key, label: choice.label, title: choice.label })),
  ]
  const siteOptions = siteName ? [{ value: siteName, label: siteName, title: siteName }] : []
  const unitWide = draft.unit === 'nth' || draft.unit === 'last'
  const saveDisabled =
    saving ||
    draft.legacyTime ||
    draft.kindId == null ||
    (draft.repeat === 'weekly_days' && draft.weekdays.length === 0) ||
    (repeating && !draft.until)

  return (
    <form
      className={styles.form}
      onSubmit={(event) => {
        if (locked) {
          event.preventDefault()
          return
        }
        formik.handleSubmit(event)
      }}>
      <div className={styles.grid}>
        <Select
          label='Category'
          required
          showSearch={false}
          disabled={locked}
          value={draft.kindId ?? undefined}
          options={kindOptions(kinds, true)}
          error={fieldErrors.kind}
          onChange={(kindId: number) => {
            const next = kinds.find((kind) => kind.id === kindId)
            const previous = kinds.find((kind) => kind.id === draft.kindId)
            const title =
              !draft.title || draft.title === previous?.name ? next?.name ?? draft.title : draft.title
            const inOffice = isInOfficeKind(next?.name)
            update({
              kindId,
              title,
              inOffice,
              ...(inOffice ? {} : { visitKindIds: [], visitKindsTouched: true }),
            })
          }}
        />
        <Input
          name='title'
          label='Title'
          disabled={locked}
          value={draft.title}
          placeholder={kinds.find((kind) => kind.id === draft.kindId)?.name}
          error={fieldErrors.title}
          onChange={(event) => update({ title: event.target.value })}
        />
        <Select
          label='Facility'
          showSearch={false}
          disabled={locked}
          value={siteName || undefined}
          options={siteOptions}
          onChange={() => undefined}
        />
        <Select
          label='Billing facility'
          showSearch={false}
          disabled={locked}
          value={siteName || undefined}
          options={siteOptions}
          onChange={() => undefined}
        />
        <div className={styles.span}>
          <Select
            label='Provider'
            required
            showSearch={false}
            disabled={locked || draft.id != null}
            value={draft.userId}
            options={providers.map((provider) => ({
              value: provider.id,
              label: provider.name,
              title: provider.name,
            }))}
            onChange={(userId: number) => update({ userId })}
          />
        </div>
      </div>

      <div className={styles.timeBox}>
        <div className={styles.timeRow}>
          <div className={styles.dateField}>
            <DatePicker
              label='Date'
              required
              allowClear={false}
              disabled={locked}
              value={dayjs(draft.date)}
              format='MM/DD/YYYY'
              showNow={false}
              onChange={(value) => value && update({ date: value.format('YYYY-MM-DD') })}
            />
          </div>
          <Segmented
            className={styles.timeMode}
            disabled={locked}
            value={draft.allDay ? 'allday' : 'timed'}
            options={[
              { label: 'Timed', value: 'timed' },
              { label: 'All day', value: 'allday' },
            ]}
            onChange={(value) => update({ allDay: value === 'allday' })}
          />
          {!draft.allDay ? (
            <>
              <div className={styles.startField}>
                <Label label='Start' id='block-start' required />
                <TimePicker
                  id='block-start'
                  className={styles.timePicker}
                  disabled={locked}
                  value={dayjs().startOf('day').add(draft.startMinutes, 'minute')}
                  format={TIME_FORMAT.TWELVE_HOUR_FORMAT}
                  use12Hours
                  minuteStep={5}
                  allowClear={false}
                  needConfirm={false}
                  onChange={(value) =>
                    value && update({ startMinutes: value.hour() * 60 + value.minute() })
                  }
                />
                {draft.legacyTime ? (
                  <div className={styles.fieldError}>
                    Pick a start and duration that stay inside the day
                  </div>
                ) : (
                  field(fieldErrors, 'start_time')
                )}
              </div>
              <div className={styles.durationField}>
                <Label label='Duration' id='block-duration' required />
                <InputNumber
                  id='block-duration'
                  className={styles.durationInput}
                  disabled={locked}
                  value={draft.duration}
                  min={5}
                  max={1440}
                  step={15}
                  controls={false}
                  addonAfter='min'
                  onChange={(value) => update({ duration: value ?? 5 })}
                />
                {field(fieldErrors, 'end_time')}
              </div>
              <div className={styles.ends}>{draftEndsLabel(draft)}</div>
            </>
          ) : null}
        </div>

        <div className={styles.divider} />

        <div className={styles.repeatBlock}>
          <Segmented
            className={styles.repeatSegment}
            disabled={locked}
            value={draft.repeat}
            options={[
              { label: 'Does not repeat', value: 'none' },
              { label: 'Repeats every…', value: 'every' },
              { label: 'Days of week', value: 'weekly_days' },
            ]}
            onChange={(value) => {
              const repeat = value as DraftRepeat
              update({
                repeat,
                weekdays:
                  repeat === 'weekly_days' && draft.weekdays.length === 0
                    ? [draft.weekdays[0]].filter(Boolean)
                    : draft.weekdays,
              })
            }}
          />
          {draft.repeat === 'every' ? (
            <div className={`${styles.inline} ${styles.tight}`}>
              <span>Repeats</span>
              <div className={styles.select120}>
                <Select
                  showSearch={false}
                  disabled={locked}
                  value={draft.every}
                  options={everyOptions(draft.every).map((option) => ({
                    ...option,
                    title: option.label,
                  }))}
                  onChange={(every: number) => update({ every })}
                />
              </div>
              <div className={unitWide ? styles.select160 : styles.select120}>
                <Select
                  showSearch={false}
                  disabled={locked}
                  value={draft.unit}
                  options={unitOptions}
                  onChange={(unit: DraftUnit) => update({ unit })}
                />
              </div>
            </div>
          ) : null}
          {draft.repeat === 'weekly_days' ? (
            <div className={styles.inline}>
              <div className={styles.select140}>
                <Select
                  showSearch={false}
                  disabled={locked}
                  value={Math.min(draft.every, 4)}
                  options={WEEK_EVERY}
                  onChange={(every: number) => update({ every })}
                />
              </div>
              <Label label='On' required />
              <Checkbox.Group
                disabled={locked}
                value={draft.weekdays}
                options={checkboxWeekdays.map((day) => ({ value: day, label: WEEKDAY_SHORT[day] }))}
                onChange={(weekdays) => update({ weekdays: weekdays as BlockDraft['weekdays'] })}
              />
              {field(fieldErrors, 'weekdays')}
            </div>
          ) : null}
          {repeating ? (
            <div className={`${styles.inline} ${styles.tight}`}>
              <Label label='Until' required />
              <div className={styles.dateField}>
                <DatePicker
                  disabled={locked}
                  value={draft.until ? dayjs(draft.until) : null}
                  format='MM/DD/YYYY'
                  allowClear={false}
                  showNow={false}
                  disabledDate={(day) => day.isBefore(dayjs(draft.date), 'day')}
                  onChange={(value) => update({ until: value ? value.format('YYYY-MM-DD') : null })}
                  error={fieldErrors.end_date}
                />
              </div>
            </div>
          ) : null}
          <div className={styles.summary}>{draftSummary(draft)}</div>
          {field(fieldErrors, 'repeat')}
        </div>
      </div>

      {draft.inOffice && visitKinds.length > 0 ? (
        <Select
          label='Preferred appointment categories'
          mode='multiple'
          allowClear
          showSearch={false}
          disabled={locked}
          placeholder='Any'
          containerClassName={styles.prefSelect}
          value={draft.visitKindIds ?? []}
          options={sortVisitKinds(visitKinds).map((kind) => ({
            value: kind.id,
            title: kind.name,
            label: (
              <span className={styles.kindCell}>
                <span className={styles.dot} style={{ background: kind.color }} />
                {kind.name}
              </span>
            ),
          }))}
          error={fieldErrors.visit_kind_ids}
          onChange={(visitKindIds: number[]) => update({ visitKindIds, visitKindsTouched: true })}
        />
      ) : null}

      <div className={styles.footer}>
        {draft.id != null ? (
          <Button danger icon={<DeleteOutlined />} disabled={saving} onClick={onDelete}>
            Delete
          </Button>
        ) : null}
        <div className={styles.spacer} />
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        {locked ? null : (
          <Button type='primary' htmlType='submit' disabled={saveDisabled} loading={saving}>
            Save
          </Button>
        )}
      </div>
    </form>
  )
}
